import React from 'react';
import { Control, Controller, FieldErrorsImpl, RegisterOptions } from 'react-hook-form';
import { Typography } from '@navi/web-ui/lib/primitives';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import cx from 'classnames';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import { SingleSelectAutoComplete } from '@navi/web-ui/lib/components';
import { AgentFormValues } from '../../interfaces/types';
import { SelectPickerValue } from '@cp/src/components/interfaces';
import { IOption } from '@cp/src/components/FilterDrawer/types';
import CustomOption from './CustomTemplateAutocomplete';

interface AgentFormAutocompleteDropdownProps {
  control: Control<AgentFormValues>;
  name: keyof AgentFormValues;
  rules: RegisterOptions;
  errors: Partial<FieldErrorsImpl<AgentFormValues>>;
  options: IOption[];
  styles: CSSModuleClasses;
  isEditDisabled: boolean | undefined;
  inputLabel: string;
  placeholder: string;
  validationMessage?: string;
  updateClearAllCallback?: () => void;
  showRequiredIcon?: boolean;
  callbackFn?: (value: string) => void;
}

const AgentFormAutocompleteDropdown: React.FC<AgentFormAutocompleteDropdownProps> = props => {
  const {
    control,
    name,
    rules,
    errors,
    isEditDisabled,
    inputLabel,
    styles,
    validationMessage,
    placeholder,
    options,
    updateClearAllCallback,
    showRequiredIcon = true,
    callbackFn
  } = props;

  return (
    <>
      <div className={styles.textContainer}>
        <Typography variant="p4" color="var(--navi-color-gray-c2)">
          {inputLabel}
        </Typography>
        {showRequiredIcon ? <RequiredIcon /> : null}
      </div>

      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, ref } }) => (
          <SingleSelectAutoComplete
            ref={ref}
            placeholder={placeholder}
            options={options}
            mainContainerClassName={styles.autoCompleteMainContainer}
            containerClassName={cx(
              styles.autoCompleteContainer,
              isEditDisabled ? styles.disabledAutoCompleteContainer : null
            )}
            selectPickerWrapperClass={cx({
              'max-h-[350px] mt-[3px]': true,
              'max-h-[360px]': name === 'reportingManagerEmailAddress'
            })}
            variant="bordered"
            selectedOption={value as SelectPickerValue}
            updateSelectedOptions={(selectedOption: SelectPickerValue) => {
              onChange(selectedOption);
              callbackFn?.(selectedOption as string);
            }}
            disabled={isEditDisabled}
            updateClearAllCallback={updateClearAllCallback}
            showCloseIconOnlyOnFocus={true}
            allowNoClickIfDisabled={true}
            showFocusOnReset={true}
            removeSelectedValueOnClear={true}
            customOptionTemplate={option => (
              <CustomOption name={name} option={option} styles={styles} />
            )}
          />
        )}
        rules={rules}
      />

      {errors && errors[name] && (
        <div className={cx([styles.errorContainer])}>
          <ErrorIconOutline />
          <Typography variant="p4" className={styles.errorText}>
            {validationMessage}
          </Typography>
        </div>
      )}
    </>
  );
};

export default AgentFormAutocompleteDropdown;
